import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { Plus, Trash2, Clock, Users, User, AlertCircle } from 'lucide-react';
import { VolunteerType, SessionVolunteerLog } from '../../types';

interface SessionVolunteerManagerProps {
  sessionId: string;
}

export const SessionVolunteerManager: React.FC<SessionVolunteerManagerProps> = ({ sessionId }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    volunteer_type_id: '',
    person_id: '',
    volunteer_count: 1,
    hours_per_volunteer: 0,
    notes: ''
  });

  const { data: volunteerTypes } = useQuery({
    queryKey: ['lookup', 'volunteer_types'],
    queryFn: async () => {
      const { data } = await supabase.from('volunteer_types').select('*').order('label');
      return (data || []) as VolunteerType[];
    }
  });

  const { data: people } = useQuery({
    queryKey: ['all-people-lookup-vols'],
    queryFn: async () => {
      const { data } = await supabase.from('people').select('id, first_name, last_name').order('last_name');
      return (data || []).map(p => ({ id: p.id, name: `${p.first_name} ${p.last_name}` }));
    }
  });

  const { data: logs, isLoading } = useQuery({
    queryKey: ['session-volunteer-logs', sessionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('session_volunteer_logs')
        .select(`
          *,
          volunteer_types:volunteer_type_id(*),
          people:person_id(first_name, last_name, email)
        `)
        .eq('session_id', sessionId)
        .order('recorded_at', { ascending: false });
      if (error) throw error;
      return (data || []) as SessionVolunteerLog[];
    }
  });

  const addLogMutation = useMutation({
    mutationFn: async (vars: typeof formData) => {
      if (!vars.volunteer_type_id) throw new Error('Volunteer category is required.');
      
      const payload = {
        session_id: sessionId,
        volunteer_type_id: vars.volunteer_type_id,
        person_id: vars.person_id || null,
        volunteer_count: vars.person_id ? 1 : vars.volunteer_count,
        hours_per_volunteer: vars.hours_per_volunteer,
        notes: vars.notes,
        created_by_email: profile?.email
      };

      const { error } = await supabase.from('session_volunteer_logs').insert([payload]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-volunteer-logs', sessionId] });
      setIsAdding(false);
      setFormData({
        volunteer_type_id: '',
        person_id: '',
        volunteer_count: 1,
        hours_per_volunteer: 0,
        notes: ''
      });
    }
  });

  const deleteLogMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('session_volunteer_logs').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-volunteer-logs', sessionId] });
    }
  });

  if (isLoading) return <div className="p-4 text-center text-[10px] font-bold text-slate-300 uppercase animate-pulse">Syncing Volunteer Logs...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
          <Users size={14} className="text-blue-600" /> Volunteering Impact Log
        </h3>
        {!isAdding && (
          <Button 
            variant="outline" 
            className="h-7 px-3 text-[9px] font-black uppercase tracking-tight" 
            icon={<Plus size={12} />} 
            onClick={() => setIsAdding(true)}
          >
            Log Impact
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="p-4 bg-slate-50 rounded border border-slate-200 space-y-4 animate-in slide-in-from-top-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Volunteer Type</label>
              <select 
                className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded font-bold text-slate-900 text-xs outline-none focus:ring-4 focus:ring-slate-100"
                value={formData.volunteer_type_id}
                onChange={e => setFormData({ ...formData, volunteer_type_id: e.target.value })}
              >
                <option value="">Select Type...</option>
                {volunteerTypes?.map(vt => <option key={vt.id} value={vt.id}>{vt.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Specific Person (Optional)</label>
              <SearchableSelect 
                options={people || []}
                value={formData.person_id}
                onChange={val => setFormData({ ...formData, person_id: val, volunteer_count: val ? 1 : formData.volunteer_count })}
                placeholder="Find person..."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {!formData.person_id && (
              <Input 
                label="# of Volunteers" 
                type="number" 
                min="1"
                value={formData.volunteer_count}
                onChange={e => setFormData({ ...formData, volunteer_count: parseInt(e.target.value) || 1 })}
                className="h-8 text-xs"
              />
            )}
            <Input 
              label="Hours Per Volunteer" 
              type="number" 
              step="0.5"
              min="0"
              value={formData.hours_per_volunteer}
              onChange={e => setFormData({ ...formData, hours_per_volunteer: parseFloat(e.target.value) || 0 })}
              className={`h-8 text-xs ${formData.person_id ? 'col-span-2' : ''}`}
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest ml-1">Notes</label>
            <textarea 
              rows={2} 
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-xs outline-none focus:ring-4 focus:ring-slate-100 resize-none shadow-inner"
              placeholder="Context about this contribution..."
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          {addLogMutation.isError && (
             <div className="p-2 bg-rose-50 border border-rose-100 rounded text-rose-600 flex items-center gap-2 text-[9px] font-bold uppercase tracking-tight">
               <AlertCircle size={12} /> {(addLogMutation.error as any).message}
             </div>
          )}

          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setIsAdding(false)} className="flex-1 h-8 text-[9px] font-black uppercase">Cancel</Button>
            <Button 
              className="flex-[2] h-8 text-[9px] font-black uppercase" 
              isLoading={addLogMutation.isPending}
              onClick={() => addLogMutation.mutate(formData)}
            >
              Commit Log
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {logs?.map(log => (
          <div key={log.id} className="p-4 bg-white border border-slate-100 rounded flex items-center justify-between group hover:border-slate-200 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-slate-50 rounded flex items-center justify-center text-slate-300">
                {log.person_id ? <User size={14} /> : <Users size={14} />}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-slate-900 uppercase tracking-tight">
                    {log.person_id ? `${log.people?.first_name} ${log.people?.last_name}` : `${log.volunteer_count} Volunteers`}
                  </span>
                  <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[8px] font-black uppercase rounded border border-blue-100">
                    {log.volunteer_types?.label}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-[9px] font-bold text-slate-400 tracking-widest uppercase">
                  <span className="flex items-center gap-1"><Clock size={10} /> {log.total_hours} Total Hours</span>
                  {log.notes && <span className="truncate max-w-[200px] italic">"{log.notes}"</span>}
                </div>
              </div>
            </div>
            <button 
              onClick={() => deleteLogMutation.mutate(log.id)}
              className="p-2 opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-500 transition-all"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {logs?.length === 0 && !isAdding && (
          <div className="p-8 text-center border border-dashed border-slate-100 rounded text-[9px] font-bold text-slate-300 uppercase tracking-widest italic">
            No volunteering impact logged for this session
          </div>
        )}
      </div>
    </div>
  );
};
